import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { AccordionGroup } from '@sola/ark-core/molecules/accordion-group/accordion-group.tsx';
import type { ChildDetails } from '@sola/schema/dotcom/children/child.ts';
import type { HouseholdMemberItem } from '@sola/schema/dotcom/children/childUtilityInterfaces.ts';
import type { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { getAgeFromBirthday } from '@sola/shared-utils/children/utils.ts';
import { titleCase } from '@sola/utils/string.ts';
interface ChildDetailsOverviewListItemProps {
  title?: string;
  children: React.ReactNode;
}
const ChildDetailsOverviewListItem: React.FC<ChildDetailsOverviewListItemProps> = t0 => {
  const $ = _c(7);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = title && <span className="label-potent">{title}{": "}</span>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <span>{children}</span>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <li className="body-sm label-neutral-mild">{t1}{t2}</li>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
interface ChildDetailsListProps {
  children: React.ReactNode;
}
const ChildDetailsList: React.FC<ChildDetailsListProps> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <ul className="p-0 list-none m-0 flex flex-col gap-snug-sm">{children}</ul>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
interface ChildDetailsListItemProps {
  title: string;
  children: React.ReactNode;
}
const ChildDetailsListItem: React.FC<ChildDetailsListItemProps> = t0 => {
  const $ = _c(7);
  const {
    title,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = title && <span className="label-potent">{title}{": "}</span>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== children) {
    t2 = <ChildDetailsList>{children}</ChildDetailsList>;
    $[2] = children;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <li className="flex flex-col body-sm label-neutral-mild">{t1}{t2}</li>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};
interface ChildDetailsAccordionSectionProps {
  children: React.ReactNode;
}
const ChildDetailsAccordionSection: React.FC<ChildDetailsAccordionSectionProps> = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="p-0 list-none m-0 flex flex-col gap-comfy-sm">{children}</div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};
const getPrimaryCareGiver = (householdMemberList: HouseholdMemberItem[]) => {
  return householdMemberList.find(member => member.isPrimaryCaregiver)?.householdMemberRole;
};
export const ChildBioAccordion = t0 => {
  const $ = _c(148);
  const {
    child,
    commitment
  } = t0;
  const {
    t
  } = useTranslation("my");
  const preferredName = child.name.preferred;
  let t1;
  if ($[0] !== t) {
    t1 = t("sponsorshipRelationship.childBio.overview.title");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t) {
    t2 = t("sponsorshipRelationship.childBio.overview.age");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== child.birthDate) {
    t3 = getAgeFromBirthday(child.birthDate);
    $[4] = child.birthDate;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <ChildDetailsOverviewListItem title={t2}>{t3}</ChildDetailsOverviewListItem>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t) {
    t5 = t("sponsorshipRelationship.childBio.overview.birthday");
    $[9] = t;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== child.birthDate || $[12] !== t) {
    t6 = t("sponsorshipRelationship.childBio.overview.birthdayValue", {
      date: child.birthDate
    });
    $[11] = child.birthDate;
    $[12] = t;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== t5 || $[15] !== t6) {
    t7 = <ChildDetailsOverviewListItem title={t5}>{t6}</ChildDetailsOverviewListItem>;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== t) {
    t8 = t("sponsorshipRelationship.childBio.overview.gender");
    $[17] = t;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== child.gender || $[20] !== t8) {
    t9 = <ChildDetailsOverviewListItem title={t8}>{child.gender}</ChildDetailsOverviewListItem>;
    $[19] = child.gender;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t) {
    t10 = t("sponsorshipRelationship.childBio.overview.location");
    $[22] = t;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== child.country || $[25] !== t10) {
    t11 = <ChildDetailsOverviewListItem title={t10}>{child.country}</ChildDetailsOverviewListItem>;
    $[24] = child.country;
    $[25] = t10;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t) {
    t12 = t("sponsorshipRelationship.childBio.overview.childId");
    $[27] = t;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== child.localId || $[30] !== t12) {
    t13 = <ChildDetailsOverviewListItem title={t12}>{child.localId}</ChildDetailsOverviewListItem>;
    $[29] = child.localId;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t11 || $[33] !== t13 || $[34] !== t4 || $[35] !== t7 || $[36] !== t9) {
    t14 = <ChildDetailsList>{t4}{t7}{t9}{t11}{t13}</ChildDetailsList>;
    $[32] = t11;
    $[33] = t13;
    $[34] = t4;
    $[35] = t7;
    $[36] = t9;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] !== commitment || $[39] !== preferredName || $[40] !== t) {
    t15 = commitment?.dates?.start && <p className="body-sm label-neutral-mild">{t("sponsorshipRelationship.childBio.overview.sponsorshipLength", {
        childName: preferredName,
        date: commitment.dates.start
      })}</p>;
    $[38] = commitment;
    $[39] = preferredName;
    $[40] = t;
    $[41] = t15;
  } else {
    t15 = $[41];
  }
  let t16;
  if ($[42] !== t14 || $[43] !== t15) {
    t16 = <ChildDetailsAccordionSection>{t14}{t15}</ChildDetailsAccordionSection>;
    $[42] = t14;
    $[43] = t15;
    $[44] = t16;
  } else {
    t16 = $[44];
  }
  let t17;
  if ($[45] !== t1 || $[46] !== t16) {
    t17 = {
      header: t1,
      children: t16
    };
    $[45] = t1;
    $[46] = t16;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== t) {
    t18 = t("sponsorshipRelationship.childBio.education.title");
    $[48] = t;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== child.education.academicPerformance || $[51] !== t) {
    t19 = child.education.academicPerformance && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.education.performance")}>{child.education.academicPerformance}</ChildDetailsListItem>;
    $[50] = child.education.academicPerformance;
    $[51] = t;
    $[52] = t19;
  } else {
    t19 = $[52];
  }
  let t20;
  if ($[53] !== child.education.formalLevel || $[54] !== t) {
    t20 = child.education.formalLevel && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.education.educationLevel")}>{titleCase(child.education.formalLevel)}</ChildDetailsListItem>;
    $[53] = child.education.formalLevel;
    $[54] = t;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] !== child.education.gradeLevel.us || $[57] !== t) {
    t21 = child.education.gradeLevel.us && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.education.gradeLevel")}>{titleCase(child.education.gradeLevel.us)}</ChildDetailsListItem>;
    $[56] = child.education.gradeLevel.us;
    $[57] = t;
    $[58] = t21;
  } else {
    t21 = $[58];
  }
  let t22;
  if ($[59] !== child.education.gradeLevel.local || $[60] !== t) {
    t22 = child.education.gradeLevel.local && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.education.localGradeName")}>{titleCase(child.education.gradeLevel.local)}</ChildDetailsListItem>;
    $[59] = child.education.gradeLevel.local;
    $[60] = t;
    $[61] = t22;
  } else {
    t22 = $[61];
  }
  let t23;
  if ($[62] !== child.interests.favoriteSchoolSubject || $[63] !== t) {
    t23 = child.interests.favoriteSchoolSubject.length > 0 && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.education.favoriteSubjects")}><ChildDetailsList>{child.interests.favoriteSchoolSubject.map(_temp)}</ChildDetailsList></ChildDetailsListItem>;
    $[62] = child.interests.favoriteSchoolSubject;
    $[63] = t;
    $[64] = t23;
  } else {
    t23 = $[64];
  }
  let t24;
  if ($[65] !== child.plannedCompletion.date || $[66] !== t) {
    t24 = child.plannedCompletion.date && <ChildDetailsListItem title={t("my:sponsorshipRelationship.childBio.education.estimatedCompletionDate")}>{t("my:sponsorshipRelationship.childBio.education.estimatedCompletionDateValue", {
        date: child.plannedCompletion.date
      })}</ChildDetailsListItem>;
    $[65] = child.plannedCompletion.date;
    $[66] = t;
    $[67] = t24;
  } else {
    t24 = $[67];
  }
  let t25;
  if ($[68] !== t) {
    t25 = t("sponsorshipRelationship.childBio.education.howYourSponsorshipHelps");
    $[68] = t;
    $[69] = t25;
  } else {
    t25 = $[69];
  }
  let t26;
  if ($[70] !== child.gender || $[71] !== preferredName || $[72] !== t) {
    t26 = t("sponsorshipRelationship.childBio.education.howYourSponsorshipHelpsSummary", {
      childName: preferredName,
      context: child.gender
    });
    $[70] = child.gender;
    $[71] = preferredName;
    $[72] = t;
    $[73] = t26;
  } else {
    t26 = $[73];
  }
  let t27;
  if ($[74] !== t25 || $[75] !== t26) {
    t27 = <ChildDetailsListItem title={t25}>{t26}</ChildDetailsListItem>;
    $[74] = t25;
    $[75] = t26;
    $[76] = t27;
  } else {
    t27 = $[76];
  }
  let t28;
  if ($[77] !== child.plannedCompletion.date || $[78] !== t) {
    t28 = child.plannedCompletion.date && <p className="body-xs label-neutral-mild"><IntlBlock i18nKey="my:sponsorshipRelationship.childBio.education.estimatedCompletionDateFootnote" components={{
        completionDateLink: <Link className="body-xs label-neutral-mild underline" href={t("my:sponsorshipRelationship.childBio.education.estimatedCompletionDateLink")} />
      }} /></p>;
    $[77] = child.plannedCompletion.date;
    $[78] = t;
    $[79] = t28;
  } else {
    t28 = $[79];
  }
  let t29;
  if ($[80] !== t19 || $[81] !== t20 || $[82] !== t21 || $[83] !== t22 || $[84] !== t23 || $[85] !== t24 || $[86] !== t27 || $[87] !== t28) {
    t29 = <ChildDetailsAccordionSection>{t19}{t20}{t21}{t22}{t23}{t24}{t27}{t28}</ChildDetailsAccordionSection>;
    $[80] = t19;
    $[81] = t20;
    $[82] = t21;
    $[83] = t22;
    $[84] = t23;
    $[85] = t24;
    $[86] = t27;
    $[87] = t28;
    $[88] = t29;
  } else {
    t29 = $[88];
  }
  let t30;
  if ($[89] !== t18 || $[90] !== t29) {
    t30 = {
      header: t18,
      children: t29
    };
    $[89] = t18;
    $[90] = t29;
    $[91] = t30;
  } else {
    t30 = $[91];
  }
  let t31;
  if ($[92] !== t) {
    t31 = t("sponsorshipRelationship.childBio.family.title");
    $[92] = t;
    $[93] = t31;
  } else {
    t31 = $[93];
  }
  let t32;
  if ($[94] !== child.householdList || $[95] !== t) {
    t32 = child.householdList?.[0]?.householdMemberList && child.householdList[0].householdMemberList.length > 0 && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.family.primaryCaregiver")}>{getPrimaryCareGiver(child.householdList[0].householdMemberList)}</ChildDetailsListItem>;
    $[94] = child.householdList;
    $[95] = t;
    $[96] = t32;
  } else {
    t32 = $[96];
  }
  let t33;
  if ($[97] !== child.householdDuty || $[98] !== t) {
    t33 = child.householdDuty.length > 0 && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.family.duties")}><ChildDetailsList>{child.householdDuty.map(_temp2)}</ChildDetailsList></ChildDetailsListItem>;
    $[97] = child.householdDuty;
    $[98] = t;
    $[99] = t33;
  } else {
    t33 = $[99];
  }
  let t34;
  if ($[100] !== t) {
    t34 = t("sponsorshipRelationship.childBio.family.howYourSponsorshipHelps");
    $[100] = t;
    $[101] = t34;
  } else {
    t34 = $[101];
  }
  let t35;
  if ($[102] !== t) {
    t35 = t("sponsorshipRelationship.childBio.family.howYourSponsorshipHelpsParagraph");
    $[102] = t;
    $[103] = t35;
  } else {
    t35 = $[103];
  }
  let t36;
  if ($[104] !== t34 || $[105] !== t35) {
    t36 = <ChildDetailsListItem title={t34}>{t35}</ChildDetailsListItem>;
    $[104] = t34;
    $[105] = t35;
    $[106] = t36;
  } else {
    t36 = $[106];
  }
  let t37;
  if ($[107] !== t32 || $[108] !== t33 || $[109] !== t36) {
    t37 = <ChildDetailsAccordionSection>{t32}{t33}{t36}</ChildDetailsAccordionSection>;
    $[107] = t32;
    $[108] = t33;
    $[109] = t36;
    $[110] = t37;
  } else {
    t37 = $[110];
  }
  let t38;
  if ($[111] !== t31 || $[112] !== t37) {
    t38 = {
      header: t31,
      children: t37
    };
    $[111] = t31;
    $[112] = t37;
    $[113] = t38;
  } else {
    t38 = $[113];
  }
  let t39;
  if ($[114] !== t) {
    t39 = t("sponsorshipRelationship.childBio.activities.title");
    $[114] = t;
    $[115] = t39;
  } else {
    t39 = $[115];
  }
  let t40;
  if ($[116] !== child.interests.christianActivityName || $[117] !== t) {
    t40 = child.interests.christianActivityName.length > 0 && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.activities.christian")}><ChildDetailsList>{child.interests.christianActivityName.map(_temp3)}</ChildDetailsList></ChildDetailsListItem>;
    $[116] = child.interests.christianActivityName;
    $[117] = t;
    $[118] = t40;
  } else {
    t40 = $[118];
  }
  let t41;
  if ($[119] !== child.interests.favoriteProjectActivity || $[120] !== t) {
    t41 = child.interests.favoriteProjectActivity.length > 0 && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.activities.project")}><ChildDetailsList>{child.interests.favoriteProjectActivity.map(_temp4)}</ChildDetailsList></ChildDetailsListItem>;
    $[119] = child.interests.favoriteProjectActivity;
    $[120] = t;
    $[121] = t41;
  } else {
    t41 = $[121];
  }
  let t42;
  if ($[122] !== child.interests.thingsILike || $[123] !== preferredName || $[124] !== t) {
    t42 = child.interests.thingsILike.length > 0 && <ChildDetailsListItem title={t("sponsorshipRelationship.childBio.activities.thingsChildLikes", {
      childName: preferredName
    })}><ChildDetailsList>{child.interests.thingsILike.map(_temp5)}</ChildDetailsList></ChildDetailsListItem>;
    $[122] = child.interests.thingsILike;
    $[123] = preferredName;
    $[124] = t;
    $[125] = t42;
  } else {
    t42 = $[125];
  }
  let t43;
  if ($[126] !== t) {
    t43 = t("sponsorshipRelationship.childBio.activities.howYourSponsorshipHelps");
    $[126] = t;
    $[127] = t43;
  } else {
    t43 = $[127];
  }
  let t44;
  if ($[128] !== child.gender || $[129] !== preferredName || $[130] !== t) {
    t44 = t("my:sponsorshipRelationship.childBio.activities.howYourSponsorshipHelpsParagraph", {
      childName: preferredName,
      context: child.gender
    });
    $[128] = child.gender;
    $[129] = preferredName;
    $[130] = t;
    $[131] = t44;
  } else {
    t44 = $[131];
  }
  let t45;
  if ($[132] !== t43 || $[133] !== t44) {
    t45 = <ChildDetailsListItem title={t43}>{t44}</ChildDetailsListItem>;
    $[132] = t43;
    $[133] = t44;
    $[134] = t45;
  } else {
    t45 = $[134];
  }
  let t46;
  if ($[135] !== t40 || $[136] !== t41 || $[137] !== t42 || $[138] !== t45) {
    t46 = <ChildDetailsAccordionSection>{t40}{t41}{t42}{t45}</ChildDetailsAccordionSection>;
    $[135] = t40;
    $[136] = t41;
    $[137] = t42;
    $[138] = t45;
    $[139] = t46;
  } else {
    t46 = $[139];
  }
  let t47;
  if ($[140] !== t39 || $[141] !== t46) {
    t47 = {
      header: t39,
      children: t46
    };
    $[140] = t39;
    $[141] = t46;
    $[142] = t47;
  } else {
    t47 = $[142];
  }
  let t48;
  if ($[143] !== t17 || $[144] !== t30 || $[145] !== t38 || $[146] !== t47) {
    const accordionItems = [t17, t30, t38, t47];
    t48 = <AccordionGroup items={accordionItems} />;
    $[143] = t17;
    $[144] = t30;
    $[145] = t38;
    $[146] = t47;
    $[147] = t48;
  } else {
    t48 = $[147];
  }
  return t48;
};
function _temp(subject) {
  return <ChildDetailsOverviewListItem key={`favorite-school-subject-${subject}`}>{subject}</ChildDetailsOverviewListItem>;
}
function _temp2(duty) {
  return <ChildDetailsOverviewListItem key={`household-duty-${duty}`}>{duty}</ChildDetailsOverviewListItem>;
}
function _temp3(activity) {
  return <ChildDetailsOverviewListItem key={`christian-activity-${activity}`}>{activity}</ChildDetailsOverviewListItem>;
}
function _temp4(activity_0) {
  return <ChildDetailsOverviewListItem key={`favorite-project-activity-${activity_0}`}>{activity_0}</ChildDetailsOverviewListItem>;
}
function _temp5(activity_1) {
  return <ChildDetailsOverviewListItem key={`things-i-like-${activity_1}`}>{activity_1}</ChildDetailsOverviewListItem>;
}